/**
 * PublicHeader - Universal header for all public-facing pages
 * Mobile-optimized with clean design
 */

import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import CorvanaLogo from '@/components/CorvanaLogo';
import LanguageSelector from '@/components/LanguageSelector';
// Lead-capture modals (radix Dialog + form, ~hidden until a CTA click) are
// lazy-loaded so they stay out of the public first-paint bundle.
const LeadCaptureModals = lazy(() => import('@/components/LeadCaptureModals'));
import { openDemoModal, DEMO_MODAL_EVENT, WAITLIST_MODAL_EVENT } from '@/components/leadCapture';
import { useTranslation } from 'react-i18next';
import {
  ChevronDown, X, Mail, BarChart3, Brain, Calculator,
  Shield, MapPin, Building, Zap, Database, Globe, Users, FileText, Lock,
  Menu, LogIn, ChevronRight, LayoutDashboard, Store, Landmark, Newspaper, BookOpen
} from 'lucide-react';
import { getPopularIndustries, getLandingPageIndustries } from '@/config/landingPageData';
import { getIndustriesCount, getDataSourcesCount } from '@/config/platformStats';
import { getIntegrationsCount } from '@/config/platformStats';

const SOLUTIONS = [
  {
    title: 'Real-Time Business Intelligence',
    description: 'Live KPI dashboards with 5-second refresh',
    href: '/real-time-business-intelligence',
    icon: BarChart3,
  },
  {
    title: 'AI Business Analytics',
    description: 'AI-powered insights for Australian SMEs',
    href: '/ai-business-analytics-australia',
    icon: Brain,
  },
  {
    title: 'Business Valuation Software',
    description: 'M&A intelligence and valuation tools',
    href: '/business-valuation-software',
    icon: Calculator,
  },
  {
    title: 'Compliance Automation',
    description: 'Automated compliance monitoring and alerts',
    href: '/compliance-automation-platform',
    icon: Shield,
  },
  {
    title: 'Multi-Location Analytics',
    description: 'Manage performance across all sites',
    href: '/multi-location-analytics',
    icon: MapPin,
  },
  {
    title: 'Grants & Compliance',
    description: 'Monthly grant matches & AI application packs',
    href: '/grants',
    icon: Landmark,
  },
  {
    title: 'Marketplace',
    description: 'Cheaper verified suppliers & vetted experts',
    href: '/marketplace',
    icon: Store,
  },
];

// Platform items with dynamic counts from single source of truth
const getPlatformItems = () => [
  {
    title: `${getIntegrationsCount()}+ Integrations`,
    description: 'Connect your existing business systems',
    href: '/integrations',
    icon: Database,
  },
  {
    title: `${getIndustriesCount()} Industries`,
    description: 'Purpose-built for your vertical',
    href: '/industries',
    icon: Building,
  },
  {
    title: `${getDataSourcesCount()}+ Data Sources`,
    description: 'Official government & industry data',
    href: '/data-sources',
    icon: Globe,
  },
  {
    title: 'AI Data Masking',
    description: 'Confidential data masked before any AI call',
    href: '/answers/ai-data-masking-for-australian-businesses',
    icon: Lock,
  },
  {
    title: 'Security & Compliance',
    description: 'AES-256-GCM · IRAP-hosted · Australian residency',
    href: '/security',
    icon: Shield,
  },
];

export default function PublicHeader({ onShowEnquiry, onShowQuickEnquiry }) {
  const { t } = useTranslation();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [solutionsOpen, setSolutionsOpen] = useState(false);
  const [platformOpen, setPlatformOpen] = useState(false);
  const [industriesExpanded, setIndustriesExpanded] = useState(false);
  // Mount the (lazy) lead-capture modals only after the first CTA event.
  const [pendingModal, setPendingModal] = useState(null);

  useEffect(() => {
    if (pendingModal) return; // mounted — the modals handle further events themselves
    const onDemo = () => setPendingModal({ type: 'demo' });
    const onWaitlist = (e) => setPendingModal({ type: 'waitlist', interest: e?.detail?.interest || 'early_access' });
    window.addEventListener(DEMO_MODAL_EVENT, onDemo);
    window.addEventListener(WAITLIST_MODAL_EVENT, onWaitlist);
    return () => {
      window.removeEventListener(DEMO_MODAL_EVENT, onDemo);
      window.removeEventListener(WAITLIST_MODAL_EVENT, onWaitlist);
    };
  }, [pendingModal]);

  // The public marketing site is always light — clear any dark-mode class the
  // CRM theme may have left on <html> so mobile visitors from ads see it correctly.
  useEffect(() => {
    document.documentElement.classList.remove('dark');
    document.body.classList.remove('dark');
  }, [location.pathname]);
  
  const isActive = (path) => location.pathname === path;
  
  // Pages with dark hero sections that need transparent header
  const isDarkHeroPage = ['/security'].includes(location.pathname);
  
  // Get industries for dropdown
  const popularIndustries = getPopularIndustries();
  const allIndustries = getLandingPageIndustries();

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      {/* Top utility bar removed for cleaner design */}

      {/* Main Navigation */}
      <div className={`transition-colors duration-300 ${
        isDarkHeroPage 
          ? 'bg-slate-900/80 backdrop-blur-lg border-b border-white/10' 
          : 'bg-white border-b border-slate-200 shadow-sm'
      }`}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-18">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 flex-shrink-0">
              <CorvanaLogo size="md" variant="light" />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden xl:flex items-center gap-1">
              {/* Solutions Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className={`gap-1 ${isDarkHeroPage ? 'text-white/90 hover:text-white hover:bg-white/10' : 'text-slate-700 hover:text-slate-900 hover:bg-slate-100'}`}>
                    Solutions <ChevronDown className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-80">
                  <DropdownMenuLabel>Business Intelligence Solutions</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {SOLUTIONS.map((item) => (
                    <DropdownMenuItem key={item.href} asChild>
                      <Link to={item.href} className="flex items-start gap-3 p-3 cursor-pointer">
                        <item.icon className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-medium text-slate-900">{item.title}</p>
                          <p className="text-xs text-slate-500">{item.description}</p>
                        </div>
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Platform Dropdown with Expandable Industries */}
              <DropdownMenu onOpenChange={(open) => { if (!open) setIndustriesExpanded(false); }}>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className={`gap-1 ${isDarkHeroPage ? 'text-white/90 hover:text-white hover:bg-white/10' : 'text-slate-700 hover:text-slate-900 hover:bg-slate-100'}`}>
                    Platform <ChevronDown className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-80">
                  <DropdownMenuLabel>Platform Capabilities</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  
                  {/* Integrations */}
                  <DropdownMenuItem asChild>
                    <Link to="/integrations" className="flex items-start gap-3 p-3 cursor-pointer">
                      <Database className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-slate-900">{getIntegrationsCount()}+ Integrations</p>
                        <p className="text-xs text-slate-500">Connect your existing business systems</p>
                      </div>
                    </Link>
                  </DropdownMenuItem>
                  
                  {/* Industries - Expandable */}
                  <div className="px-1">
                    <button
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIndustriesExpanded(!industriesExpanded); }}
                      className="w-full flex items-center justify-between gap-3 p-3 rounded-md hover:bg-slate-100 transition-colors"
                    >
                      <div className="flex items-start gap-3">
                        <Building className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                        <div className="text-left">
                          <p className="font-medium text-sm text-slate-900">{getIndustriesCount()} Industries</p>
                          <p className="text-xs text-slate-500">Purpose-built for your vertical</p>
                        </div>
                      </div>
                      <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${industriesExpanded ? 'rotate-90' : ''}`} />
                    </button>
                    
                    {/* Expanded Industries Grid */}
                    {industriesExpanded && (
                      <div className="mt-2 mb-2 p-3 bg-slate-50 rounded-lg max-h-64 overflow-y-auto">
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Popular Industries</p>
                        <div className="grid grid-cols-2 gap-1.5 mb-3">
                          {popularIndustries.slice(0, 8).map((industry) => (
                            <Link
                              key={industry.id}
                              to={`/industries/${industry.id}`}
                              className="flex items-center gap-2 p-2 rounded-md hover:bg-white text-sm text-slate-700 hover:text-slate-900 transition-colors"
                            >
                              {industry.icon && <industry.icon className="w-4 h-4 text-slate-400" />}
                              <span className="truncate">{industry.name}</span>
                            </Link>
                          ))}
                        </div>
                        <DropdownMenuSeparator />
                        <Link
                          to="/industries"
                          className="flex items-center justify-center gap-2 p-2 mt-2 rounded-md bg-primary/5 hover:bg-primary/10 text-sm font-medium text-primary transition-colors"
                        >
                          View All {getIndustriesCount()} Industries
                          <ChevronRight className="w-4 h-4" />
                        </Link>
                      </div>
                    )}
                  </div>
                  
                  {/* Data Sources */}
                  <DropdownMenuItem asChild>
                    <Link to="/data-sources" className="flex items-start gap-3 p-3 cursor-pointer">
                      <Globe className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-slate-900">{getDataSourcesCount()}+ Data Sources</p>
                        <p className="text-xs text-slate-500">Official government & industry data</p>
                      </div>
                    </Link>
                  </DropdownMenuItem>
                  
                  {/* AI Data Masking */}
                  <DropdownMenuItem asChild>
                    <Link to="/answers/ai-data-masking-for-australian-businesses" className="flex items-start gap-3 p-3 cursor-pointer">
                      <Lock className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-slate-900">AI Data Masking</p>
                        <p className="text-xs text-slate-500">Confidential data masked before any AI call</p>
                      </div>
                    </Link>
                  </DropdownMenuItem>

                  {/* Security */}
                  <DropdownMenuItem asChild>
                    <Link to="/security" className="flex items-start gap-3 p-3 cursor-pointer">
                      <Shield className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-slate-900">Security & Compliance</p>
                        <p className="text-xs text-slate-500">AES-256-GCM · IRAP-hosted · Australian residency</p>
                      </div>
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Pricing Link */}
              <Link to="/pricing">
                <Button variant="ghost" className={`${isDarkHeroPage ? 'text-white/90 hover:text-white hover:bg-white/10' : 'text-slate-700 hover:text-slate-900 hover:bg-slate-100'} ${isActive('/pricing') ? (isDarkHeroPage ? 'bg-white/10' : 'bg-slate-100') : ''}`}>
                  Pricing
                </Button>
              </Link>

              {/* Resources Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className={`gap-1 ${isDarkHeroPage ? 'text-white/90 hover:text-white hover:bg-white/10' : 'text-slate-700 hover:text-slate-900 hover:bg-slate-100'}`}>
                    Resources <ChevronDown className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-64">
                  <DropdownMenuLabel>Learn & Explore</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {/* Interactive Demo hidden for market discretion */}
                  <DropdownMenuItem asChild>
                    <Link to="/brief" className="flex items-start gap-3 p-3 cursor-pointer" data-testid="header-brief-link">
                      <Newspaper className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-slate-900">The Corvana Brief</p>
                        <p className="text-xs text-slate-500">Free personalised AI business news, daily</p>
                      </div>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/faq" className="flex items-start gap-3 p-3 cursor-pointer">
                      <FileText className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-slate-900">FAQ</p>
                        <p className="text-xs text-slate-500">Common questions answered</p>
                      </div>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel>Guides</DropdownMenuLabel>
                  <DropdownMenuItem asChild>
                    <Link to="/ai-for-franchise-restaurants" className="flex items-start gap-3 p-3 cursor-pointer" data-testid="header-guide-franchise">
                      <BookOpen className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-slate-900">AI for Franchise Restaurants</p>
                        <p className="text-xs text-slate-500">Franchise & quick service restaurants</p>
                      </div>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/ai-business-intelligence" className="flex items-start gap-3 p-3 cursor-pointer" data-testid="header-guide-bi">
                      <BookOpen className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-slate-900">AI Business Intelligence</p>
                        <p className="text-xs text-slate-500">Real-time, AI-driven analytics</p>
                      </div>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/ai-consulting" className="flex items-start gap-3 p-3 cursor-pointer" data-testid="header-guide-consulting">
                      <BookOpen className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-slate-900">AI Consulting & Advisory</p>
                        <p className="text-xs text-slate-500">Strategy plus a working platform</p>
                      </div>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/ai-for-australian-business" className="flex items-start gap-3 p-3 cursor-pointer" data-testid="header-guide-australia">
                      <BookOpen className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-slate-900">AI for Australian Business</p>
                        <p className="text-xs text-slate-500">Local data residency & benchmarks</p>
                      </div>
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              
              <Link to="/about">
                <Button variant="ghost" className={`${isDarkHeroPage ? 'text-white/90 hover:text-white hover:bg-white/10' : 'text-slate-700 hover:text-slate-900 hover:bg-slate-100'} ${isActive('/about') ? (isDarkHeroPage ? 'bg-white/10' : 'bg-slate-100') : ''}`}>
                  About
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="ghost" className={`${isDarkHeroPage ? 'text-white/90 hover:text-white hover:bg-white/10' : 'text-slate-700 hover:text-slate-900 hover:bg-slate-100'} ${isActive('/contact') ? (isDarkHeroPage ? 'bg-white/10' : 'bg-slate-100') : ''}`}>
                  Contact
                </Button>
              </Link>
            </nav>

            {/* Desktop CTA Buttons */}
            <div className="hidden xl:flex items-center gap-2">
              <Link to="/login">
                <Button variant="ghost" className={isDarkHeroPage ? 'text-white/90 hover:text-white hover:bg-white/10' : 'text-slate-700 hover:text-slate-900 hover:bg-slate-100'}>
                  <LogIn className="w-4 h-4 mr-2" />
                  Login
                </Button>
              </Link>

              <Button
                variant="outline"
                onClick={openDemoModal}
                data-testid="header-book-demo"
                className={isDarkHeroPage ? 'border-white/30 text-white hover:bg-white/10 rounded-full' : 'rounded-full'}
              >
                Book a Demo
              </Button>

              {onShowEnquiry ? (
                <Button onClick={onShowEnquiry} className="liquid-button">
                  Get Early Access
                </Button>
              ) : (
                <Link to="/get-started">
                  <Button className="liquid-button">Get Started Free</Button>
                </Link>
              )}
            </div>

            {/* Mobile Actions - Mail icon + Hamburger */}
            <div className="flex xl:hidden items-center gap-1">
              {/* Mail Icon - Links to Contact page */}
              <Link 
                to="/contact"
                className={`p-2 transition-colors ${isDarkHeroPage ? 'text-white/80 hover:text-white' : 'text-slate-600 hover:text-primary'}`}
                aria-label="Contact us"
              >
                <Mail className="h-5 w-5" />
              </Link>
              
              {/* Hamburger Menu Button */}
              <Button
                variant="ghost"
                className={`p-2 ${isDarkHeroPage ? 'text-white hover:bg-white/10' : 'text-slate-700'}`}
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="xl:hidden border-t border-slate-200 fixed inset-x-0 top-[56px] h-[calc(100dvh-56px)] bg-white overflow-y-auto z-[60]">
            <div className="container mx-auto px-4 py-4 space-y-1 text-slate-900">
              
              {/* Quick Actions - Login Only */}
              <div className="flex gap-2 pb-3 border-b border-slate-100">
                <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="flex-1">
                  <Button variant="outline" className="w-full h-10 gap-2">
                    <LogIn className="w-4 h-4" />
                    Login
                  </Button>
                </Link>
                <Link to="/get-started" onClick={() => setMobileMenuOpen(false)} className="flex-1">
                  <Button className="w-full h-10 gap-2 liquid-button">Get Started Free
                  </Button>
                </Link>
              </div>

              {/* Language Selector - Hidden for now
              <div className="py-3 border-b border-slate-100">
                <div className="flex items-center justify-between px-1">
                  <span className="text-sm font-medium text-slate-600">Language</span>
                  <LanguageSelector />
                </div>
              </div>
              */}

              {/* Solutions Section - Collapsible */}
              <div className="py-2">
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setSolutionsOpen(!solutionsOpen); }}
                  className="w-full flex items-center justify-between px-2 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 rounded-lg transition-colors"
                >
                  <span>Solutions</span>
                  <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${solutionsOpen ? 'rotate-180' : ''}`} />
                </button>
                {solutionsOpen && (
                  <div className="mt-1 space-y-0.5 pl-2">
                    {SOLUTIONS.map((item) => (
                      <Link
                        key={item.href}
                        to={item.href}
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-slate-50 transition-colors"
                      >
                        <item.icon className="w-5 h-5 text-primary flex-shrink-0" />
                        <span className="text-sm text-slate-700">{item.title}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Platform Section - Collapsible */}
              <div className="py-2 border-t border-slate-100">
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setPlatformOpen(!platformOpen); }}
                  className="w-full flex items-center justify-between px-2 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 rounded-lg transition-colors"
                >
                  <span>Platform</span>
                  <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${platformOpen ? 'rotate-180' : ''}`} />
                </button>
                {platformOpen && (
                  <div className="mt-1 space-y-0.5 pl-2">
                    {getPlatformItems().map((item) => (
                      <Link
                        key={item.href}
                        to={item.href}
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-slate-50 transition-colors"
                      >
                        <item.icon className="w-5 h-5 text-primary flex-shrink-0" />
                        <span className="text-sm text-slate-700">{item.title}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* Quick Links */}
              <div className="py-2 border-t border-slate-100 space-y-0.5">
                <Link to="/pricing" onClick={() => setMobileMenuOpen(false)}>
                  <div className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-slate-50 transition-colors">
                    <span className="text-sm font-medium text-slate-700">Pricing</span>
                  </div>
                </Link>
                <Link to="/brief" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-brief-link">
                  <div className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-slate-50 transition-colors">
                    <Newspaper className="w-4 h-4 text-primary" />
                    <span className="text-sm font-medium text-slate-700">The Corvana Brief</span>
                  </div>
                </Link>
                <Link to="/about" onClick={() => setMobileMenuOpen(false)}>
                  <div className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-slate-50 transition-colors">
                    <span className="text-sm font-medium text-slate-700">About Us</span>
                  </div>
                </Link>
                <Link to="/contact" onClick={() => setMobileMenuOpen(false)}>
                  <div className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-slate-50 transition-colors">
                    <span className="text-sm font-medium text-slate-700">Contact Us</span>
                  </div>
                </Link>
              </div>

              {/* CTA Button */}
              <div className="pt-4 pb-6 border-t border-slate-100 space-y-2">
                <Button
                  variant="outline"
                  className="w-full h-12 text-base rounded-full"
                  data-testid="mobile-book-demo"
                  onClick={() => { setMobileMenuOpen(false); openDemoModal(); }}
                >
                  Book a Demo
                </Button>
                <Link to="/get-started" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="w-full liquid-button h-12 text-base">Get Started Free</Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
      {pendingModal && (
        <Suspense fallback={null}>
          <LeadCaptureModals initialOpen={pendingModal} />
        </Suspense>
      )}
    </header>
  );
}
